import Minze, { Attrs, EventListeners, MinzeElement, Reactive } from 'minze'
import { v4 as uuid } from 'uuid'
import {
  FlowCore,
  IFlowContainer,
  IFlowCore,
  IFlowCoreConfig,
  IFlowTransition,
} from '../../../modules/epflow'
import { Locale } from '../../../modules/locale'
import MainModule from '../../../modules/main'
import { recursiveCleanObject } from '../../../modules/utils'
import { isRepresentationValid, validateRepresentation } from './representation-validation'

Locale.getInstance().registerKeySet('fr', 'lvcCreateExhibitionContainer', {
  exhibitionCreationFailed: "Impossible de créer l'exposition. Veuillez réessayer plus tard.",
  submitBlockedInvalid:
    "L'exposition comporte des erreurs. Veuillez les corriger avant de soumettre.",
})

Locale.getInstance().registerKeySet('en', 'lvcCreateExhibitionContainer', {
  exhibitionCreationFailed: 'Failed to create exhibition. Please try again later.',
  submitBlockedInvalid: 'The exhibition contains errors. Please fix them before submitting.',
})

export interface LvcCreateExhibitionContainer {}

const STEPS = {
  CREATE_EXHIBITION_DESCRIPTION: 'createExhibitionDescription',
  CREATE_EXHIBITION_DETAILS: 'createExhibitionDetails',
  CREATE_EXHIBITION_IMAGE: 'createExhibitionAddImage',
  ADD_EXHIBITION_CONTRIBUTORS: 'addExhibitionContributors',
  CREATE_EXHIBITION_FIRST_RECAP: 'eventFirstRecap',
  EXHIBITION_SUBMITTED: 'eventSubmitted',
}

const CORE_CONFIG: IFlowCoreConfig = {
  initialStep: STEPS.CREATE_EXHIBITION_DESCRIPTION,
  eventType: 'exhibition',
  calcProgress: (data: any, currentStep: IFlowTransition, transitions: IFlowTransition[]) => {
    let indexOf = transitions.indexOf(currentStep)
    return Math.round(100 * (indexOf / (transitions.length - 1)))
  },
  transitions: [
    {
      stepName: STEPS.CREATE_EXHIBITION_DESCRIPTION,
      elementName: (contextData: any) => `
        <lvc-create-event-description 
          flow-step="${STEPS.CREATE_EXHIBITION_DESCRIPTION}" 
          flow-group="${contextData.flowGroup}" 
          data-type="exhibition">
        </lvc-create-event-description>
      `,
      transitionNext: async (data: any) => {
        return STEPS.CREATE_EXHIBITION_DETAILS
      },
      transitionPrev: async (data: any) => {
        return STEPS.CREATE_EXHIBITION_DESCRIPTION
      },
    },
    {
      stepName: STEPS.CREATE_EXHIBITION_DETAILS,
      elementName: (contextData: any) => `
        <lvc-create-event-add-representation 
          flow-step="${STEPS.CREATE_EXHIBITION_DETAILS}" 
          flow-group="${contextData.flowGroup}" 
          data-type="exhibition">
        </lvc-create-event-add-representation>
      `,
      transitionNext: async (data: any) => {
        return STEPS.CREATE_EXHIBITION_IMAGE
      },
      transitionPrev: async (data: any) => {
        return STEPS.CREATE_EXHIBITION_DESCRIPTION
      },
    },
    {
      stepName: STEPS.CREATE_EXHIBITION_IMAGE,
      elementName: (contextData: any) => `
        <lvc-create-event-add-image 
          flow-step="${STEPS.CREATE_EXHIBITION_IMAGE}" 
          flow-group="${contextData.flowGroup}" 
          data-type="exhibition">
        </lvc-create-event-add-image>
      `,
      transitionNext: async (data: any) => {
        return STEPS.ADD_EXHIBITION_CONTRIBUTORS
      },
      transitionPrev: async (data: any) => {
        return STEPS.CREATE_EXHIBITION_DETAILS
      },
    },
    {
      stepName: STEPS.ADD_EXHIBITION_CONTRIBUTORS,
      elementName: (contextData: any) => `
        <lvc-create-event-add-contributors 
          flow-step="${STEPS.ADD_EXHIBITION_CONTRIBUTORS}" 
          flow-group="${contextData.flowGroup}" 
          data-type="exhibition">
        </lvc-create-event-add-contributors>
      `,
      transitionNext: async (data: any) => {
        return STEPS.CREATE_EXHIBITION_FIRST_RECAP
      },
      transitionPrev: async (data: any) => {
        return STEPS.CREATE_EXHIBITION_IMAGE
      },
    },
    {
      stepName: STEPS.CREATE_EXHIBITION_FIRST_RECAP,
      elementName: (contextData: any) => `
        <lvc-create-event-first-recap 
          flow-step="${STEPS.CREATE_EXHIBITION_FIRST_RECAP}" 
          flow-group="${contextData.flowGroup}" 
          data-type="exhibition">
        </lvc-create-event-first-recap>
      `,
      transitionNext: async (data: any) => {
        return STEPS.EXHIBITION_SUBMITTED
      },
      transitionPrev: async (data: any) => {
        return STEPS.ADD_EXHIBITION_CONTRIBUTORS
      },
    },
    {
      stepName: STEPS.EXHIBITION_SUBMITTED,
      elementName: (contextData: any) => `
        <lvc-create-event-submitted 
          flow-step="${STEPS.EXHIBITION_SUBMITTED}" 
          flow-group="${contextData.flowGroup}" 
          data-type="exhibition">
        </lvc-create-event-submitted>
      `,
      transitionNext: async (data: any) => {
        return STEPS.EXHIBITION_SUBMITTED
      },
      transitionPrev: async (data: any) => {
        return STEPS.CREATE_EXHIBITION_FIRST_RECAP
      },
    },
  ] as IFlowTransition[],

  onSave: async (data: unknown, instance: IFlowCore): Promise<void> => {
    const draftData = data

    await MainModule.getInstance()
      .getAuth()
      .setUserCustomDocument(
        `exhibition_drafts/${instance.entryId}`,
        recursiveCleanObject({
          draftData,
          lastModified: new Date().toISOString(),
        })
      )
  },
  onCommit: async (data: unknown, instance: IFlowCore): Promise<void> => {
    const mainModule = MainModule.getInstance()
    const flowCore = MainModule.getInstance().getFlowManager().getCore('exhibitionCreation')

    // Final submission gate (PRO-48): block a submission that still has errors.
    const representations = (data as any)?.createExhibitionDetails?.representations || []
    const allRepresentationsValid =
      representations.length > 0 &&
      representations.every((representation: any) =>
        isRepresentationValid(validateRepresentation(representation, 'exhibition'))
      )
    if (!allRepresentationsValid) {
      mainModule.showToastAlert(
        Locale.getKey('lvcCreateExhibitionContainer.submitBlockedInvalid'),
        5000,
        true
      )
      return
    }

    //Call the backend to create the exhibition
    const response = await mainModule
      .getAuth()
      .createContentItem(flowCore?.entryId || uuid(), data, 'exhibition')

    if (!response.success) {
      // If unsuccessful, show error (draft is preserved)
      console.error('Error during exhibition creation:', response.message)
      mainModule.showToastAlert(
        Locale.getKey('lvcCreateExhibitionContainer.exhibitionCreationFailed'),
        5000,
        true
      )
    } else {
      // If successful, proceed to next step
      flowCore?.next()
    }
  },
}

export class LvcCreateExhibitionContainer
  extends MinzeElement
  implements MinzeElement, IFlowContainer
{
  flowGroup: string = 'exhibitionCreation'
  attrs: Attrs = []
  reactive: Reactive = ['stepName']
  flowCore: IFlowCore | null = null
  renderElement: string = ''
  draftId: string | null = null
  _authHandler: any = null

  async onReady() {
    const mainModule = MainModule.getInstance()
    this._authHandler = this.handleAuthChanged.bind(this)
    mainModule.getEE().on('lvc:user_state_changed', this._authHandler)

    this.handleAuthChanged({
      isLoggedIn: await mainModule.getAuth().isLoggedIn(),
    })
  }

  handleAuthChanged = async (data: any): Promise<void> => {
    if (!data.isLoggedIn) {
      console.warn('User is not logged in. Aborting exhibition draft fetch.')
      return
    }

    const mainModule = MainModule.getInstance()

    // Create a new FlowCore instance and put it in the FlowManager.
    this.flowCore = new FlowCore({
      ...CORE_CONFIG,
      flowContainer: this,
    } as IFlowCoreConfig)
    MainModule.getInstance().getFlowManager().putCore(this.flowCore)

    // Get the draftId from URL parameters.
    const urlParams = new URLSearchParams(window.location.search)
    this.draftId = urlParams.get('draftId')

    if (this.draftId) {
      try {
        const draftData = await mainModule.getAuth().fetchDraftExhibitionById(this.draftId)
        if (draftData) {
          this.flowCore.entryId = this.draftId
          // Load the draft data into the FlowCore.
          this.flowCore.loadDraft(draftData)
        }
      } catch (error) {
        console.error(`Error fetching exhibition draft for ${this.draftId}:`, error)
      }
    } else {
      this.flowCore.entryId = uuid()
    }

    // Now that draftData is loaded (or not), initialize FlowCore.
    await this.flowCore.initialize()

    // Notify other components that FlowCore is ready.
    Minze.cast('lvc:flowcore-ready', this.flowCore)
  }

  async onContainerUpdate(data: any, step: IFlowTransition): Promise<void> {
    window.scrollTo(0, 0)
    let el: string = step?.elementName?.({ flowGroup: this.flowGroup }) || '<span>no element</span>'
    this.renderElement = el
    this.stepName = step.stepName
    this.rerender()
  }

  html = () => {
    let progress: number = this.flowCore?.getProgress() ?? 0
    return `
      <div class="lvc-create-exhibition-container">
        <div class="lvc-content">
          ${this.renderElement}
        </div>
        <div class="lvc-footer-container">
          <lvc-footer-navigation
            progress="${progress}"
            flow-group="${this.flowGroup}"
            step="${this.stepName}"
            data-type="exhibition">
          </lvc-footer-navigation>
        </div>
      </div>
    `
  }

  css = () => `
    .lvc-create-exhibition-container {
      display: flex;
      flex-direction: column;
      min-height: 100vh;
      min-width: 100%;
      align-items: center;
      position: relative;
    }
    .lvc-content {
      display: flex;
      max-height: 100%;
      flex-direction: column;
      max-width: 600px;
      position: relative;
      padding-bottom: 200px;
    }
    .lvc-footer-container {
      display: flex;
      justify-content: center;
      flex-direction: column;
      margin-top: auto;
      width: 100%;
      position: fixed;
      bottom: 0;
    }
  `

  eventListeners: EventListeners = []
}
